import { Mixpanel } from './mixpanel';

/**
 * Analytics Tracking Utility
 *
 * Centralized Mixpanel integration with buffering to reduce network chatter.
 * Buffer flushes after 10 events or 60 seconds (whichever comes first).
 */

type QueuedEvent = {
    name: string;
    props?: Record<string, any>;
    ts: number;
};

const EVENT_BUFFER: QueuedEvent[] = [];
const MAX_BUFFER_SIZE = 10;
const FLUSH_INTERVAL_MS = 60_000;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const flushEvents = () => {
    try {
        if (!EVENT_BUFFER.length) {
            return;
        }

        const pending = [...EVENT_BUFFER];
        EVENT_BUFFER.length = 0;

        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }

        pending.forEach(({ name, props, ts }) => {
            Mixpanel.track(name, { ...props, _ts: ts });
            if (import.meta.env.DEV) {
                console.log('📊 Analytics Event (flushed):', name, props);
            }
        });
    } catch (error) {
        console.error('Analytics flush error:', error);
    }
};

const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushEvents();
    }, FLUSH_INTERVAL_MS);
};

export const flushAnalyticsQueue = () => flushEvents();

/**
 * Identify user (call after login)
 */
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
    try {
        Mixpanel.identify(userId);
        if (properties) {
            Mixpanel.people.set(properties);
        }
    } catch (error) {
        console.error('User identification error:', error);
    }
};

/**
 * Reset analytics (call on logout)
 */
export const resetAnalytics = () => {
    try {
        EVENT_BUFFER.length = 0;
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        Mixpanel.reset();
    } catch (error) {
        console.error('Analytics reset error:', error);
    }
};

/**
 * Track an event
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    try {
        EVENT_BUFFER.push({
            name: eventName,
            props: properties,
            ts: Date.now()
        });

        if (import.meta.env.DEV) {
            console.log('📥 Queued analytics event:', eventName, properties);
        }

        if (EVENT_BUFFER.length >= MAX_BUFFER_SIZE) {
            flushEvents();
        } else {
            scheduleFlush();
        }
    } catch (error) {
        console.error('Analytics tracking error:', error);
    }
};

/**
 * Auth / Login Events
 */
export const trackLoginAttempt = (properties: {
    phone_number: string;
    domain?: string;
    service_provider_id?: string;
    user_id?: string;
    metadata?: Record<string, any>;
}) => {
    trackEvent('login_attempt', properties);
};

export const trackOtpVerified = (properties: {
    phone_number: string;
    domain?: string;
    service_provider_id?: string;
    user_id?: string;
    verification_status?: 'success' | 'failure';
    metadata?: Record<string, any>;
}) => {
    trackEvent('otp_verified', properties);
};

export const trackLoginSuccess = (properties: {
    user_id?: string;
    phone_number: string;
    domain?: string;
    service_provider_id?: string;
    metadata?: Record<string, any>;
}) => {
    trackEvent('login_success', properties);
};

export const trackLoginFailure = (properties: {
    phone_number: string;
    domain: string;
    service_provider_id?: string;
    reason?: string;
    metadata?: Record<string, any>;
}) => {
    trackEvent('login_failure', properties);
};

/**
 * Dashboard engagement
 */
export const trackDashboardTileClicked = (properties: {
    service_provider_id: string;
    mobile_number: string;
    tile_name: string;
    tile_id?: string;
    user_id?: string;
    meta?: Record<string, any>;
}) => {
    trackEvent('dashboard_tile_clicked', properties);
};

/**
 * Meeting Platform Events
 */

export const trackGoogleMeetSectionViewed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    user_role?: string;
}) => {
    trackEvent('google_meet_section_viewed', properties);
};

export const trackGoogleMeetJoinClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    meeting_id: string;
    meeting_title?: string;
    user_role?: string;
}) => {
    trackEvent('google_meet_join_clicked', properties);
};

export const trackZoomSectionViewed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    user_role?: string;
    is_authenticated: boolean;
}) => {
    trackEvent('zoom_section_viewed', properties);
};

export const trackZoomMeetingJoinClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    meeting_id: string;
    meeting_title?: string;
    user_role?: string;
}) => {
    trackEvent('zoom_meeting_join_clicked', properties);
};

export const trackMeetPlusSectionViewed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    user_role?: string;
    is_enabled: boolean;
}) => {
    trackEvent('meetplus_section_viewed', properties);
};

export const trackMeetPlusJoinClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    meeting_id: string;
    meeting_title: string;
    user_role?: string;
}) => {
    trackEvent('meetplus_join_clicked', properties);
};

export const trackMeetPlusCreateClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    user_role?: string;
}) => {
    trackEvent('meetplus_create_clicked', properties);
};

export const trackMeetPlusPromotionViewed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    feature_enabled: boolean;
}) => {
    trackEvent('meetplus_promotion_viewed', properties);
};

export const trackMeetPlusContactClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    contact_type: 'email' | 'phone';
    feature_enabled: boolean;
}) => {
    trackEvent('meetplus_contact_clicked', properties);
};

/**
 * Chequered Events
 */

export const trackChequeredIconClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    subscription_name: string;
    user_role?: string;
    is_enabled: boolean;
}) => {
    trackEvent('chequered_icon_clicked', properties);
};

export const trackChequeredLandingViewed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    is_enabled: boolean;
    is_mobile: boolean;
}) => {
    trackEvent('chequered_landing_viewed', properties);
};

export const trackChequeredFeatureClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    feature_name: string;
    feature_type: 'primary' | 'secondary';
    is_enabled: boolean;
}) => {
    trackEvent('chequered_feature_clicked', properties);
};

export const trackChequeredPromotionViewed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    page_type: 'landing' | 'feature_page';
    feature_enabled: boolean;
}) => {
    trackEvent('chequered_promotion_viewed', properties);
};

export const trackChequeredContactClicked = (properties: {
    user_id: string;
    service_provider_id: string;
    contact_type: 'email' | 'phone';
    feature_enabled: boolean;
}) => {
    trackEvent('chequered_contact_clicked', properties);
};

export const trackChequeredMobileWarningShown = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    page_type: string;
}) => {
    trackEvent('chequered_mobile_warning_shown', properties);
};

export const trackChequeredMobileWarningDismissed = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    page_type: string;
}) => {
    trackEvent('chequered_mobile_warning_dismissed', properties);
};

/**
 * Chequered – Base payload for all Chequered events.
 * Include where available: service_provider_id, service_provider_name, user_id, user_name, subscription_id, subscription_name, user_role.
 */
export type ChequeredBasePayload = {
    service_provider_id: string;
    user_id: string;
    subscription_id?: string;
    subscription_name?: string;
    service_provider_name?: string;
    user_name?: string;
    user_role?: string;
};

/**
 * Chequered – Puzzles (main puzzle list / category)
 */
export const trackChequeredPageViewed = (p: ChequeredBasePayload & { page: 'puzzles' | 'teaching' | 'neo' | 'subscription-data' | 'chess-basics' | 'analysis' | 'create' | 'import'; is_enabled: boolean }) => {
    trackEvent('chequered_page_viewed', p);
};

export const trackChequeredCategorySelected = (p: ChequeredBasePayload & { category: string; category_label?: string }) => {
    trackEvent('chequered_category_selected', p);
};

export const trackChequeredPuzzleOpened = (p: ChequeredBasePayload & { puzzle_id: string; puzzle_rating?: number; puzzle_complexity?: string; puzzle_category?: string; source: 'list' | 'fullscreen' }) => {
    trackEvent('chequered_puzzle_opened', p);
};

export const trackChequeredPuzzleSolved = (p: ChequeredBasePayload & { puzzle_id: string; move_count?: number }) => {
    trackEvent('chequered_puzzle_solved', p);
};

export const trackChequeredPuzzleBookmarked = (p: ChequeredBasePayload & { puzzle_id: string; action: 'add' | 'remove' }) => {
    trackEvent('chequered_puzzle_bookmarked', p);
};

export const trackChequeredSeedPuzzlesClicked = (p: ChequeredBasePayload) => {
    trackEvent('chequered_seed_puzzles_clicked', p);
};

export const trackChequeredSeedPuzzlesCompleted = (p: ChequeredBasePayload & { count: number; success: boolean }) => {
    trackEvent('chequered_seed_puzzles_completed', p);
};

export const trackChequeredImportClicked = (p: ChequeredBasePayload & { import_type: 'csv' | 'excel' }) => {
    trackEvent('chequered_import_clicked', p);
};

export const trackChequeredImportCompleted = (p: ChequeredBasePayload & { import_type: 'csv' | 'excel'; count: number; success: boolean }) => {
    trackEvent('chequered_import_completed', p);
};

export const trackChequeredTemplateDownloaded = (p: ChequeredBasePayload & { template_type: 'csv' | 'excel' }) => {
    trackEvent('chequered_template_downloaded', p);
};

/**
 * Chequered – Class puzzles (subscription-specific puzzle list)
 */
export const trackChequeredClassPuzzlesViewed = (p: ChequeredBasePayload) => {
    trackEvent('chequered_class_puzzles_viewed', p);
};

export const trackChequeredPuzzleAddedToClass = (p: ChequeredBasePayload & { puzzle_id: string }) => {
    trackEvent('chequered_puzzle_added_to_class', p);
};

export const trackChequeredPuzzleRemovedFromClass = (p: ChequeredBasePayload & { puzzle_id: string }) => {
    trackEvent('chequered_puzzle_removed_from_class', p);
};

export const trackChequeredPuzzleMarkedSolved = (p: ChequeredBasePayload & { puzzle_id: string; context: 'class_list' | 'collaborative' }) => {
    trackEvent('chequered_puzzle_marked_solved', p);
};

/**
 * Chequered – Collaborative puzzle solving
 */
export const trackChequeredCollabSessionStarted = (p: ChequeredBasePayload & { session_id: string; puzzle_id: string }) => {
    trackEvent('chequered_collab_session_started', p);
};

export const trackChequeredCollabSessionJoined = (p: ChequeredBasePayload & { session_id: string; role: 'teacher' | 'student' }) => {
    trackEvent('chequered_collab_session_joined', p);
};

export const trackChequeredCollabPuzzleSwitched = (p: ChequeredBasePayload & { session_id: string; puzzle_id: string; puzzle_description?: string }) => {
    trackEvent('chequered_collab_puzzle_switched', p);
};

export const trackChequeredCollabShareLinkCopied = (p: ChequeredBasePayload & { session_id: string }) => {
    trackEvent('chequered_collab_share_link_copied', p);
};

export const trackChequeredCollabSessionEnded = (p: ChequeredBasePayload & { session_id: string; ended_by: 'teacher' | 'student' }) => {
    trackEvent('chequered_collab_session_ended', p);
};

export const trackChequeredCollabPuzzleSolved = (p: ChequeredBasePayload & { session_id: string; puzzle_id: string; role: 'teacher' | 'student' }) => {
    trackEvent('chequered_collab_puzzle_solved', p);
};

/**
 * Chequered – Articles (Chess Basics)
 */
export const trackChequeredArticlesViewed = (p: ChequeredBasePayload) => {
    trackEvent('chequered_articles_viewed', p);
};

export const trackChequeredArticleOpened = (p: ChequeredBasePayload & { article_id: string; article_title?: string }) => {
    trackEvent('chequered_article_opened', p);
};

export const trackChequeredArticlesSeeded = (p: ChequeredBasePayload & { count: number }) => {
    trackEvent('chequered_articles_seeded', p);
};

/**
 * Chequered – Game analysis
 */
export const trackChequeredGameAnalysisViewed = (p: ChequeredBasePayload) => {
    trackEvent('chequered_game_analysis_viewed', p);
};

export const trackChequeredGameAnalysisStarted = (p: ChequeredBasePayload & { move_count?: number; has_fen?: boolean }) => {
    trackEvent('chequered_game_analysis_started', p);
};

export const trackChequeredGameAnalysisCompleted = (p: ChequeredBasePayload & { move_count: number; accuracy?: number; blunders?: number; mistakes?: number }) => {
    trackEvent('chequered_game_analysis_completed', p);
};

/**
 * Meeting Tab Selection Events
 */

export const trackMeetingTabSelected = (properties: {
    user_id: string;
    service_provider_id: string;
    subscription_id: string;
    tab_name: 'google_meet' | 'zoom' | 'meetplus';
    user_role?: string;
}) => {
    trackEvent('meeting_tab_selected', properties);
};
